import { useState, useMemo, useEffect, useRef } from "react"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { Plus, Search, LayoutList, Kanban, TrendingUp } from "lucide-react"
import { LoadMoreButton } from "@/components/load-more-button"
import { ExportButton } from "@/components/export-button"
import { useDeals } from "@/hooks/use-deals"
import { useDealTypes } from "@/hooks/use-deal-types"
import { useFilterParams } from "@/hooks/use-filter-params"
import { useCustomFields } from "@/hooks/use-custom-fields"
import { useCustomFieldColumns } from "@/hooks/use-custom-field-columns"
import { useColumnPreferences } from "@/hooks/use-column-preferences"
import { useBulkDelete, useBulkUpdate, useBulkTag } from "@/hooks/use-bulk-actions"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { EmptyState } from "@/components/empty-state"
import { ColumnSelector, type ColumnOption } from "@/components/column-selector"
import { SortableHeader } from "@/components/sortable-header"
import { BulkActionBar, buildDeleteAction, buildTagAction, buildAssignAction, buildStatusAction } from "@/components/bulk-action-bar"
import { BulkDeleteDialog } from "@/components/bulk-delete-dialog"
import { BulkAssignDialog } from "@/components/bulk-assign-dialog"
import { BulkStatusDialog } from "@/components/bulk-status-dialog"
import { BulkTagDialog } from "@/components/bulk-tag-dialog"
import { FilterPanel, SelectFilter, DateRangeFilter, NumberRangeFilter } from "@/components/filters"
import { createSelectColumn } from "@/lib/bulk-selection"
import { formatCurrency, formatDate, sortingStateToParam, buildDetailUrl } from "@/lib/utils"
import { toast } from "sonner"
import {
  type SortingState,
  type ColumnDef,
  type RowSelectionState,
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table"
import type { Deal } from "@/lib/types"

const DEAL_FILTER_KEYS = [
  "status", "phase_id", "owner_id", "value_min", "value_max",
  "expected_close_from", "expected_close_to", "created_at_from", "created_at_to",
] as const

const columnHelper = createColumnHelper<Deal>()

const BASE_COLUMN_IDS = ["name", "phase", "value", "probability", "status", "owner", "expected_close_date"]
const DEFAULT_VISIBLE = ["name", "phase", "value", "probability", "status", "owner", "expected_close_date"]

const BASE_COLUMN_LABELS: Record<string, string> = {
  name: "Nazwa",
  phase: "Etap",
  value: "Wartosc",
  probability: "Prawdopodobienstwo",
  status: "Status",
  owner: "Opiekun",
  expected_close_date: "Planowane zamkniecie",
}

export function DealsListPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [search, setSearch] = useState("")
  const [sorting, setSorting] = useState<SortingState>([])
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [showAssignDialog, setShowAssignDialog] = useState(false)
  const [showStatusDialog, setShowStatusDialog] = useState(false)
  const [showTagDialog, setShowTagDialog] = useState(false)

  const [cursor, setCursor] = useState<string | undefined>()
  const [allDeals, setAllDeals] = useState<Deal[]>([])
  const prevKeyRef = useRef("")

  const { filters, setFilter, clearFilters, activeFilterCount } = useFilterParams(DEAL_FILTER_KEYS)

  const { data: dealTypesData } = useDealTypes()
  const dealPhases = useMemo(() => {
    const dt = dealTypesData?.data
    if (!dt?.length) return []
    return dt.flatMap((t) => t.phases.map((p) => ({ value: p.id, label: `${p.name}` })))
  }, [dealTypesData])

  const sortParam = sortingStateToParam(sorting)
  const { data, isLoading, isFetching } = useDeals({
    search: search || undefined,
    sort: sortParam,
    cursor,
    ...filters,
  })

  const filterKey = JSON.stringify({ search, sort: sortParam, ...filters })
  useEffect(() => {
    if (filterKey !== prevKeyRef.current) {
      prevKeyRef.current = filterKey
      setCursor(undefined)
      setAllDeals([])
    }
  }, [filterKey])

  useEffect(() => {
    if (data?.data) {
      if (cursor) {
        setAllDeals((prev) => [...prev, ...data.data])
      } else {
        setAllDeals(data.data)
      }
    }
  }, [data])

  const bulkDeleteMutation = useBulkDelete("deals")
  const bulkUpdateMutation = useBulkUpdate("deals")
  const bulkTagMutation = useBulkTag("deals")

  const deals = allDeals
  const selectedIds = Object.keys(rowSelection).filter((k) => rowSelection[k])
  const selectedCount = selectedIds.length

  // Custom fields
  const { data: cfData } = useCustomFields("deals")
  const customFields = cfData?.data
  const customFieldCols = useCustomFieldColumns<Deal>(customFields)

  const baseColumns = useMemo(
    () => [
      columnHelper.accessor("name", {
        header: ({ column }) => <SortableHeader column={column}>{t("deals.title")}</SortableHeader>,
        cell: (info) => (
          <div>
            <span className="font-medium text-foreground">
              {info.getValue()}
            </span>
            {info.row.original.company && (
              <p className="text-xs text-muted-foreground">
                {info.row.original.company.name}
              </p>
            )}
          </div>
        ),
      }),
      columnHelper.accessor("phase", {
        header: () => t("deals.phase"),
        enableSorting: false,
        cell: (info) => {
          const phase = info.getValue()
          return (
            <div className="flex items-center gap-2">
              <div className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: phase.color }} />
              <span className="text-sm text-foreground">{phase.name}</span>
            </div>
          )
        },
      }),
      columnHelper.accessor("value", {
        header: ({ column }) => <SortableHeader column={column}>{t("deals.value")}</SortableHeader>,
        cell: (info) => (
          <span className="text-sm font-mono tabular-nums text-foreground">
            {info.getValue() != null ? formatCurrency(info.getValue()!, info.row.original.currency) : "\u2014"}
          </span>
        ),
      }),
      columnHelper.accessor("probability", {
        header: ({ column }) => <SortableHeader column={column}>{t("deals.probability")}</SortableHeader>,
        cell: (info) => (
          <span className="text-sm font-mono tabular-nums text-muted-foreground">
            {info.getValue() != null ? `${info.getValue()}%` : "\u2014"}
          </span>
        ),
      }),
      columnHelper.accessor("status", {
        header: ({ column }) => <SortableHeader column={column}>Status</SortableHeader>,
        cell: (info) => {
          const status = info.getValue()
          const statusClasses: Record<string, string> = {
            open: "border-primary text-primary",
            won: "border-green-500 text-green-500",
            lost: "border-red-500 text-red-500",
          }
          const labels: Record<string, string> = {
            open: "Otwarta",
            won: "Wygrana",
            lost: "Przegrana",
          }
          return (
            <Badge variant="outline" className={`text-xs ${statusClasses[status]}`}>
              {labels[status]}
            </Badge>
          )
        },
      }),
      columnHelper.accessor("owner", {
        header: () => t("deals.owner"),
        enableSorting: false,
        cell: (info) => {
          const owner = info.getValue()
          return (
            <span className="text-sm text-muted-foreground">
              {owner ? `${owner.first_name} ${owner.last_name}` : "\u2014"}
            </span>
          )
        },
      }),
      columnHelper.accessor("expected_close_date", {
        header: ({ column }) => <SortableHeader column={column}>{t("deals.expectedClose")}</SortableHeader>,
        cell: (info) => (
          <span className="text-sm text-muted-foreground">
            {info.getValue() ? formatDate(info.getValue()!) : "\u2014"}
          </span>
        ),
      }),
    ],
    [t]
  )

  const allColumns = useMemo(
    () => [createSelectColumn<Deal>(), ...baseColumns, ...customFieldCols] as ColumnDef<Deal, unknown>[],
    [baseColumns, customFieldCols]
  )

  const allColumnIds = useMemo(() => {
    const cfIds = customFieldCols.map((c) => (c as { id?: string }).id ?? "")
    return [...BASE_COLUMN_IDS, ...cfIds]
  }, [customFieldCols])

  const columnOptions: ColumnOption[] = useMemo(() => {
    const base = BASE_COLUMN_IDS.map((id) => ({
      id,
      label: BASE_COLUMN_LABELS[id],
    }))
    const cf = (customFields ?? []).map((f) => ({
      id: `cf_${f.key}`,
      label: f.label?.pl || f.label?.en || f.key,
      isCustomField: true,
    }))
    return [...base, ...cf]
  }, [customFields])

  const {
    columnVisibility,
    setColumnVisibility,
    visibleCount,
    totalCount,
    toggleColumn,
    showAll,
    hideAll,
    isColumnVisible,
  } = useColumnPreferences("deals", allColumnIds, DEFAULT_VISIBLE)

  const handleBulkDelete = async () => {
    const result = await bulkDeleteMutation.mutateAsync({ ids: selectedIds })
    toast.success(`Usunieto ${result.deleted} transakcji`)
    setRowSelection({})
  }

  const handleBulkAssign = async (userId: string) => {
    const result = await bulkUpdateMutation.mutateAsync({
      ids: selectedIds,
      changes: { owner_id: userId },
    })
    toast.success(`Zaktualizowano ${result.updated} transakcji`)
    setRowSelection({})
  }

  const handleBulkStatus = async (status: string) => {
    const result = await bulkUpdateMutation.mutateAsync({
      ids: selectedIds,
      changes: { status },
    })
    toast.success(`Zaktualizowano ${result.updated} transakcji`)
    setRowSelection({})
  }

  const handleBulkTag = async (tagNames: string[]) => {
    const result = await bulkTagMutation.mutateAsync({
      ids: selectedIds,
      tag_names: tagNames,
    })
    toast.success(`Otagowano ${result.tagged} transakcji`)
    setRowSelection({})
  }

  const bulkActions = [
    buildAssignAction(() => setShowAssignDialog(true), "Przypisz opiekuna"),
    buildStatusAction(() => setShowStatusDialog(true)),
    buildTagAction(() => setShowTagDialog(true)),
    buildDeleteAction(() => setShowDeleteDialog(true)),
  ]

  const table = useReactTable({
    data: deals,
    columns: allColumns,
    state: { sorting, columnVisibility, rowSelection },
    onSortingChange: setSorting,
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    enableRowSelection: true,
    getCoreRowModel: getCoreRowModel(),
    manualSorting: true,
    getRowId: (row) => row.id,
  })

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h1 className="page-title">
            {t("deals.title")}
          </h1>
          {/* View toggle */}
          <div className="flex items-center overflow-hidden rounded-lg border border-input">
            <button className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium bg-primary text-primary-foreground">
              <LayoutList size={14} />
              {t("deals.listView")}
            </button>
            <button
              onClick={() => navigate("/deals/board")}
              className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
            >
              <Kanban size={14} />
              {t("deals.boardView")}
            </button>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <ExportButton
            entity="deals"
            filters={{ search, ...filters }}
            visibleColumns={Object.keys(columnVisibility).filter((k) => columnVisibility[k] !== false)}
          />
          <Button onClick={() => navigate("/deals/new")}>
            <Plus size={16} /> {t("deals.new")}
          </Button>
        </div>
      </div>

      {/* Search + Column selector */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-xs">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("common.search")}
            className="h-9 w-full rounded-lg border border-input bg-transparent pl-9 pr-3 text-sm text-foreground outline-none placeholder:text-muted-foreground"
          />
        </div>
        <ColumnSelector
          columns={columnOptions}
          visibleCount={visibleCount}
          totalCount={totalCount}
          isColumnVisible={isColumnVisible}
          onToggleColumn={toggleColumn}
          onShowAll={showAll}
          onHideAll={hideAll}
        />
      </div>

      {/* Advanced Filters */}
      <FilterPanel activeCount={activeFilterCount} onClear={clearFilters}>
        <SelectFilter
          label="Status"
          value={filters.status || ""}
          onChange={(v) => setFilter("status", v)}
          options={[
            { value: "open", label: "Otwarte" },
            { value: "won", label: "Wygrane" },
            { value: "lost", label: "Przegrane" },
          ]}
          placeholder="Wszystkie statusy"
        />
        <SelectFilter
          label="Etap"
          value={filters.phase_id || ""}
          onChange={(v) => setFilter("phase_id", v)}
          options={dealPhases}
          placeholder="Wszystkie etapy"
        />
        <NumberRangeFilter
          label="Wartosc (PLN)"
          minValue={filters.value_min || ""}
          maxValue={filters.value_max || ""}
          onMinChange={(v) => setFilter("value_min", v)}
          onMaxChange={(v) => setFilter("value_max", v)}
          minPlaceholder="Od"
          maxPlaceholder="Do"
        />
        <DateRangeFilter
          label="Planowane zamkniecie"
          fromValue={filters.expected_close_from || ""}
          toValue={filters.expected_close_to || ""}
          onFromChange={(v) => setFilter("expected_close_from", v)}
          onToChange={(v) => setFilter("expected_close_to", v)}
        />
        <DateRangeFilter
          label="Data utworzenia"
          fromValue={filters.created_at_from || ""}
          toValue={filters.created_at_to || ""}
          onFromChange={(v) => setFilter("created_at_from", v)}
          onToChange={(v) => setFilter("created_at_to", v)}
        />
      </FilterPanel>

      {/* Bulk action bar */}
      <BulkActionBar
        selectedCount={selectedCount}
        onDeselectAll={() => setRowSelection({})}
        actions={bulkActions}
      />

      {/* Table */}
      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      ) : deals.length === 0 ? (
        !search && activeFilterCount === 0 ? (
          <EmptyState
            icon={TrendingUp}
            heading="Brak transakcji"
            description="Utworz pierwsza szanse sprzedazy"
            actionLabel="Nowa szansa"
            onAction={() => navigate("/deals/new")}
          />
        ) : (
          <div className="flex flex-col items-center gap-3 rounded-xl border border-dashed py-12 text-muted-foreground">
            <p className="text-sm">{t("common.noResults")}</p>
            <Button variant="outline" onClick={() => navigate("/deals/new")}>
              <Plus size={16} /> {t("deals.new")}
            </Button>
          </div>
        )
      ) : (
        <div className="surface-card overflow-hidden">
          <table className="w-full">
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id} className="bg-muted/30">
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      className="px-4 py-2.5 text-left text-xs font-medium text-muted-foreground"
                    >
                      {flexRender(header.column.columnDef.header, header.getContext())}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((row) => (
                <tr
                  key={row.id}
                  onClick={() => navigate(buildDetailUrl("/deals", row.original.id, { sort: sortingStateToParam(sorting), filters: filters as Record<string, string | undefined> }))}
                  className="cursor-pointer border-t transition-colors hover:bg-muted/30"
                  data-state={row.getIsSelected() ? "selected" : undefined}
                >
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id} className="px-4 py-3">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <LoadMoreButton
        hasMore={data?.meta.has_more ?? false}
        isLoading={isFetching && !!cursor}
        onClick={() => {
          if (data?.meta.next_cursor) setCursor(data.meta.next_cursor)
        }}
      />

      {/* Bulk action dialogs */}
      <BulkDeleteDialog
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        count={selectedCount}
        entityLabel="transakcji"
        onConfirm={handleBulkDelete}
      />
      <BulkAssignDialog
        open={showAssignDialog}
        onOpenChange={setShowAssignDialog}
        count={selectedCount}
        onConfirm={handleBulkAssign}
        fieldLabel="Opiekun"
      />
      <BulkStatusDialog
        open={showStatusDialog}
        onOpenChange={setShowStatusDialog}
        count={selectedCount}
        options={[
          { value: "open", label: "Otwarta" },
          { value: "won", label: "Wygrana" },
          { value: "lost", label: "Przegrana" },
        ]}
        onConfirm={handleBulkStatus}
      />
      <BulkTagDialog
        open={showTagDialog}
        onOpenChange={setShowTagDialog}
        count={selectedCount}
        onConfirm={handleBulkTag}
      />
    </div>
  )
}
